import type { ParsedUrlQuery } from 'querystring';

import { normalizeChainIdFromZeroXApi } from '@/lib/api/0x-cross-chain/constants';

import { isAdvancedClosedOrderStatus } from '@/constants/advancedOrder';
import { CHAIN_ID_PER_NETWORK_URL_PATH, SUPPORTED_CHAIN_IDS_LIMIT_ORDERS } from '@/constants/chain';

import { DIVIDER_CHAR } from './constants';
import { timeFilters, type FiltersReducerState } from './OrderHistory/Filters/reducer';

/**
 * Parse a history chain identifier that may arrive as numeric string
 * (e.g. "8453") or network slug (e.g. "solana"). The 0x Solana chain ID resolves
 * to our internal one in both forms.
 */
export function parseHistoryChainId(
  rawChainId: string | number | null | undefined,
): number | undefined {
  if (!rawChainId) return undefined;

  if (typeof rawChainId === 'number') {
    return Number.isSafeInteger(rawChainId) && rawChainId > 0
      ? normalizeChainIdFromZeroXApi(rawChainId)
      : undefined;
  }

  const normalized = rawChainId.trim().toLowerCase();
  if (!normalized) return undefined;
  const chainIdFromSlug = CHAIN_ID_PER_NETWORK_URL_PATH[normalized];
  if (typeof chainIdFromSlug === 'number') return chainIdFromSlug;

  if (!/^\d+$/.test(normalized)) return undefined;

  const chainIdFromNumber = Number(normalized);
  if (Number.isSafeInteger(chainIdFromNumber) && chainIdFromNumber > 0)
    return normalizeChainIdFromZeroXApi(chainIdFromNumber);

  return undefined;
}

/** A positive integer page from the URL, or `undefined` when absent or malformed. */
export function parseHistoryPage(value: string | string[] | undefined): number | undefined {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) return undefined;
  const page = Number(value);
  return Number.isSafeInteger(page) && page > 0 ? page : undefined;
}

/** A supported time filter from the URL, or all time (`0`) when absent or unsupported. */
export function parseHistoryTime(
  value: string | string[] | undefined,
): FiltersReducerState['time'] {
  if (typeof value !== 'string') return 0;
  return timeFilters.find((filter) => filter.value.toString() === value)?.value ?? 0;
}

/**
 * Pagination, filter and search state shared by every trade-history surface,
 * parsed from the page's URL query.
 */
export type HistoryQueryState = {
  initialPage?: number;
  initialFilters?: FiltersReducerState;
  initialSearchTerm?: string;
  initialOpenOrderChainId: number | null;
};

export function parseHistoryQuery(
  query: ParsedUrlQuery,
  { allowStatus }: { allowStatus: boolean },
): HistoryQueryState {
  const initialPage = parseHistoryPage(query.page);
  const time = parseHistoryTime(query.time);
  const networks =
    typeof query.networks === 'string' ? query.networks.split(DIVIDER_CHAR).filter(Boolean) : [];
  const orderTypes =
    typeof query.orderTypes === 'string'
      ? query.orderTypes.split(DIVIDER_CHAR).filter(Boolean)
      : [];
  const status =
    allowStatus && isAdvancedClosedOrderStatus(query.advancedOrderStatus)
      ? query.advancedOrderStatus
      : undefined;

  const initialFilters: FiltersReducerState | undefined =
    !time && !networks.length && !orderTypes.length && !status
      ? undefined
      : { time, networks, orderTypes, ...(status && { status }) };

  const initialSearchTerm = typeof query.searchTerm === 'string' ? query.searchTerm : undefined;

  // Use the first supported network from the URL, or all networks when absent/invalid.
  const supportedNetworksForOpenOrders = networks
    .map(Number)
    .filter(
      (chainId) => Number.isFinite(chainId) && SUPPORTED_CHAIN_IDS_LIMIT_ORDERS.includes(chainId),
    );

  return {
    ...(initialPage && { initialPage }),
    ...(initialFilters && { initialFilters }),
    ...(initialSearchTerm && { initialSearchTerm }),
    initialOpenOrderChainId: supportedNetworksForOpenOrders[0] ?? null,
  };
}
